import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdRotateLeft } from "react-icons/md";
import { Button } from "../../../components/core/basic/Button";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { type AppRow, createAppColumnHelper, useClientTable } from "../../../components/table/tableFeatures";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { commaSearchFilterFn } from "../../../components/table/filterFns";
import { resetTable } from "../../../components/table/utils";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { HStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { useMarketplaceLabel } from "../../../utils/marketplaceConstants";
import { type AdsAccount, type CountryId } from "../../../zodios/apiDefinitions/api/advertisingAccounts";
import { useAdvertisingAccounts } from "./useAdvertisingAccounts";

const columnHelper = createAppColumnHelper<AdsAccount>();

type IdsKind = "entityIds" | "profileIds";

// Accessors must depend on the row alone: v9 caches each row's values until `data` changes, so
// anything language-dependent is resolved in `cell` and `sortFn` instead.
const idsOf = (account: AdsAccount, kind: IdsKind) => (account[kind].length ? account[kind] : undefined);

function IdList({ lines }: { lines: string[] }) {
  return (
    <ul>
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

/** The read-only list of the org's Amazon Ads accounts and their IDs in each marketplace. */
export function AdvertisingAccountsTable() {
  const { t } = useTranslation(["onboarding", "common"]);
  const marketplaceLabel = useMarketplaceLabel();
  const { data: accounts, isLoading } = useAdvertisingAccounts();

  const columns = useMemo(() => {
    /** `Country: ID` for each ID, ordered by country name in the current language. */
    const formatIds = (ids: CountryId[] = []) =>
      ids
        .map(({ countryCode, id }) => ({ country: marketplaceLabel(countryCode), id }))
        .sort((a, b) => a.country.localeCompare(b.country))
        .map(({ country, id }) => `${country}: ${id}`);

    const byFormattedIds = (kind: IdsKind) => (a: AppRow<AdsAccount>, b: AppRow<AdsAccount>) =>
      formatIds(a.original[kind]).join().localeCompare(formatIds(b.original[kind]).join());

    return [
      columnHelper.accessor((account) => account.accountName ?? undefined, {
        id: "accountName",
        header: t(($) => $.onboarding.Name),
        size: 250,
        enableHiding: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
      }),
      columnHelper.accessor((account) => account.countryCodes?.join(", ") || undefined, {
        id: "countryCodes",
        header: t(($) => $.onboarding.Marketplaces),
        size: 160,
        sortFn: "alphanumeric",
        sortUndefined: "last",
      }),
      columnHelper.accessor("adsAccountId", {
        header: t(($) => $.onboarding["Ad Account ID"]),
        size: 350,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor((account) => idsOf(account, "entityIds"), {
        id: "entityIds",
        header: t(($) => $.onboarding["Entity IDs By Marketplace"]),
        size: 300,
        sortFn: byFormattedIds("entityIds"),
        sortUndefined: "last",
        cell: ({ getValue }) => <IdList lines={formatIds(getValue())} />,
      }),
      columnHelper.accessor((account) => idsOf(account, "profileIds"), {
        id: "profileIds",
        header: t(($) => $.onboarding["Profile IDs By Marketplace"]),
        size: 300,
        sortFn: byFormattedIds("profileIds"),
        sortUndefined: "last",
        cell: ({ getValue }) => <IdList lines={formatIds(getValue())} />,
      }),
    ];
  }, [t, marketplaceLabel]);

  const globalFilterFn = useMemo(
    () =>
      commaSearchFilterFn<AdsAccount>((account) => [
        account.accountName ?? "",
        account.adsAccountId,
        ...[...account.entityIds, ...account.profileIds].flatMap(({ countryCode, id }) => [
          marketplaceLabel(countryCode),
          id,
        ]),
      ]),
    [marketplaceLabel],
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "advertisingAccountsTable",
    initialState: {
      pagination: { pageIndex: 0, pageSize: 10 },
      sorting: [{ id: "accountName", desc: false }],
    },
  });

  const table = useClientTable({
    data: accounts,
    columns,
    globalFilterFn,
    getRowId: (account) => account.adsAccountId,
    initialState,
    state,
    ...setters,
    meta: { showSkeleton: isLoading },
  });

  return (
    <div>
      <HStack padding={"small"} fontSize={"large"}>
        <Button
          variant={"contentOnly"}
          aria-label={t(($) => $.common.table["Reset table filters"])}
          onPress={() => resetTable(table)}
          fontSize={"xxlarge"}
        >
          <MdRotateLeft />
        </Button>
        <RT8ColumnSelector table={table} />
        <SearchInput
          aria-label={t(($) => $.common.actions["Search"])}
          isExpandable
          css={{ flex: "0 1 350px" }}
          value={table.state.globalFilter ?? ""}
          onChange={table.setGlobalFilter}
        />
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} />
    </div>
  );
}
